import React, { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, usd, formatApiErrorDetail, itemKey, personKey, uid } from "../../lib/api";
import { regStatusCls, REG_STATUS_OPTIONS, fmtTs } from "./adminUtils";
import { StatusChanger } from "./StatusChanger";

const GUARDIAN_FIELDS = ["first_name", "last_name", "email", "phone"];
const PARTICIPANT_FIELDS = ["first_name", "last_name", "tshirt_size", "shorts_size", "socks_size"];
const editInput = "rounded-lg border-2 border-[#E2EBF0] px-3 py-2 text-sm";

const toDraft = (order) => {
  const d = JSON.parse(JSON.stringify(order));
  d.participants = d.participants.map((p) => ({ ...p, _k: uid("edit") }));
  return d;
};

function EditForm({ order, onSaved, onCancel }) {
  const [draft, setDraft] = useState(() => toDraft(order));
  const [saving, setSaving] = useState(false);
  const setGuardian = (k, v) => setDraft((d) => ({ ...d, guardian: { ...d.guardian, [k]: v } }));
  const setParticipant = (i, k, v) => setDraft((d) => ({ ...d, participants: d.participants.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)) }));

  const save = async () => {
    setSaving(true);
    try {
      const remark = window.prompt("Optional remark describing this change:", "") || "";
      const { data } = await api.put(`/admin/registrations/${order.reference}`, { guardian: draft.guardian, participants: draft.participants.map(({ _k, ...p }) => p), remark });
      toast.success("Registration updated");
      onSaved(data.order);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-4 rounded-xl border-2 border-[#008057]/30 p-4 space-y-3" data-testid="reg-edit-form">
      <div className="font-bold text-[#03281B]">Edit registration</div>
      <div className="grid grid-cols-2 gap-2">
        {GUARDIAN_FIELDS.map((k) => <input key={k} data-testid={`edit-guardian-${k}`} className={editInput} placeholder={`Guardian ${k.replace("_", " ")}`} value={draft.guardian[k] || ""} onChange={(e) => setGuardian(k, e.target.value)} />)}
      </div>
      {draft.participants.map((p, i) => (
        <div key={p._k} className="grid grid-cols-2 sm:grid-cols-3 gap-2 border-t border-slate-100 pt-2">
          {PARTICIPANT_FIELDS.map((k) => <input key={k} className={editInput} placeholder={`P${i + 1} ${k.replace("_", " ")}`} value={p[k] || ""} onChange={(e) => setParticipant(i, k, e.target.value)} />)}
        </div>
      ))}
      <div className="flex gap-2">
        <button data-testid="save-edit-btn" onClick={save} disabled={saving} className="rounded-full bg-[#008057] text-white px-5 py-2 font-bold text-sm disabled:opacity-60">{saving ? "Saving..." : "Save changes"}</button>
        <button onClick={onCancel} className="rounded-full border-2 border-[#E2EBF0] px-5 py-2 font-bold text-sm">Cancel edit</button>
      </div>
    </div>
  );
}

const AdminNotes = ({ notes }) => (
  <div className="mt-3 text-xs text-slate-500 border-t border-slate-100 pt-2" data-testid="reg-admin-notes">
    <div className="font-semibold text-slate-600 mb-1">Admin remarks</div>
    {notes.map((n) => (<div key={`${n.ts}-${n.action}-${n.actor}`}>• <b>{n.action?.replace(/_/g, " ")}</b> by {n.actor} — {n.remark || "(no remark)"} <span className="text-slate-400">{fmtTs(n.ts)}</span></div>))}
  </div>
);

const ParticipantDetails = ({ participants }) => (
  <div className="mt-4 space-y-3">
    {participants.map((p) => (
      <div key={personKey(p)} className="rounded-xl bg-[#F2F8F5] p-3">
        <div className="font-semibold text-[#03281B]">{p.first_name} {p.last_name} <span className="text-xs text-slate-400">({p.gender}, DOB {p.dob})</span></div>
        <div className="text-xs text-slate-500">Kit: T {p.tshirt_size} / Shorts {p.shorts_size} / Socks {p.socks_size}</div>
        {p.medical && !p.medical.none && <div className="text-xs text-red-600 mt-1">Medical: {p.medical.details}</div>}
        {(p.items || []).map((it) => <div key={itemKey(it)} className="flex justify-between text-xs mt-1"><span>{it.label} - {it.detail}</span><span>{usd(it.amount)}</span></div>)}
      </div>
    ))}
  </div>
);

const isOpen = (status) => !["cancelled", "annulled"].includes(status);

const ModalActions = ({ o, actions, onEdit, onClose }) => (
  <div className="flex flex-wrap gap-3 mt-6">
    {o.registration_status !== "confirmed" && isOpen(o.registration_status) && <button data-testid="confirm-payment-btn" onClick={() => actions.confirm(o.reference)} className="rounded-full bg-[#008057] text-white px-5 py-2.5 font-bold">Confirm Registration</button>}
    <button data-testid="edit-reg-btn" onClick={onEdit} className="rounded-full border-2 border-[#008057] text-[#008057] px-5 py-2.5 font-bold">Edit</button>
    {o.registration_status !== "cancelled" && <button data-testid="cancel-reg-btn" onClick={() => actions.cancel(o.reference)} className="rounded-full border-2 border-red-300 text-red-600 px-5 py-2.5 font-bold">Cancel</button>}
    {isOpen(o.registration_status) && <button data-testid="annul-reg-btn" onClick={() => actions.annul(o.reference)} className="rounded-full border-2 border-amber-400 text-amber-700 px-5 py-2.5 font-bold">Annul</button>}
    <button data-testid="delete-reg-btn" onClick={() => actions.remove(o.reference)} className="rounded-full bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 font-bold flex items-center gap-2"><Trash2 size={16} /> Delete</button>
    <button data-testid="close-reg-modal" onClick={onClose} className="rounded-full border-2 border-[#E2EBF0] text-slate-600 px-6 py-2.5 font-bold">Close</button>
  </div>
);

export function RegistrationModal({ order: o, actions, onClose, onUpdated, onStatusChanged }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="reg-detail-modal">
        <div className="flex items-center justify-between">
          <div><div className="font-mono font-bold text-xl text-[#03281B]">{o.reference}</div><div className="text-sm text-slate-500">{o.created_at?.slice(0, 10)} &middot; {o.payment_method === "stripe" ? "Credit Card" : "Bank Transfer"}</div></div>
          <span className={`text-xs font-bold px-3 py-1 rounded-full ${regStatusCls(o.registration_status)}`}>{o.registration_status.replace("_", " ")}</span>
        </div>
        <div className="mt-4 text-sm"><strong>Guardian:</strong> {o.guardian.first_name} {o.guardian.last_name} &middot; {o.guardian.email} &middot; {o.guardian.phone}</div>
        <div data-testid="reg-terms-accepted" className="mt-1 text-xs text-slate-400">Terms &amp; Privacy accepted: {o.terms_accepted_at ? `${fmtTs(o.terms_accepted_at, 19)} UTC` : "—"}</div>
        {editing && <EditForm order={o} onSaved={(u) => { setEditing(false); onUpdated(u); }} onCancel={() => setEditing(false)} />}
        {o.admin_notes?.length > 0 && <AdminNotes notes={o.admin_notes} />}
        <ParticipantDetails participants={o.participants} />
        <div className="flex justify-between font-bold text-lg mt-4 border-t pt-3"><span>Total</span><span>{usd(o.total)}</span></div>
        <StatusChanger kind="registrations" reference={o.reference} current={o.registration_status} statuses={REG_STATUS_OPTIONS} testPrefix="reg" onDone={onStatusChanged} />
        <ModalActions o={o} actions={actions} onEdit={() => setEditing(true)} onClose={onClose} />
      </div>
    </div>
  );
}
